import {
	asyncSingleSelectColumn,
	booleanColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	singleSelectColumn,
	textColumn
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { CatalogPartApi } from "src/api/generated/erp/parts/catalogPart/api/catalogPartApi.ts";
import { concatWithPipe } from "src/utils/strings.tsx";
import { CatalogPart } from "src/api/generated/erp/db/types/tables/catalogPart.ts";
import {
	getPartRevisionLineAcquisitionMethodLabels
} from "src/api/generated/erp/db/types/enums/partRevisionLineAcquisitionMethod.ts";
import { AavoDataGridApi } from "src/components/common/dataGrid/AavoDataGridApi.ts";
import { AavoGridColDef } from "src/components/common/dataGrid/types.tsx";
import { BomLineGroup } from "src/api/generated/erp/db/types/tables/bomLineGroup.ts";
import { CatalogPartRevisionLineView } from "src/api/generated/erp/db/types/tables/catalogPartRevisionLineView.ts";
import { CatalogPartView } from "src/api/generated/erp/db/types/tables/catalogPartView.ts";

export interface CatalogPartRevisionBomDataGridRow extends CatalogPartRevisionLineView {
	rowId?: string;
}

export function getCatalogPartRevisionBomDataGridColumns({
															 showInDesignUnit = false,
															 bomLineGroupOptions
														 }: {
	showInDesignUnit?: boolean;
	bomLineGroupOptions: Array<BomLineGroup>;
}): AavoGridColDef<CatalogPartRevisionBomDataGridRow>[] {
	return [
		integerColumn({
			field: "referenceNo",
			headerName: i18n.t("reference_no"),
			editable: true,
			validate: "required"
		}),
		asyncSingleSelectColumn<CatalogPartRevisionBomDataGridRow, CatalogPartView, number>({
			afterColumn: "referenceNo",
			disableClearable: true,
			field: "lineCatalogPartId",
			headerName: i18n.t("part"),
			getOptionKey: (o: CatalogPart) => o.catalogPartId,
			fetchOptions: CatalogPartApi.getCatalogPartOptions,
			getOptionLabel: (option) => concatWithPipe(option.partNo, option.description_1, option.description_2),
			getRowLabel: (row) => concatWithPipe(row.linePartNo, row.linePartDescription_1, row.linePartDescription_2),
			updateRowOnChange: (row, value) => {
				return {
					...row,
					linePartNo: value?.partNo ?? "",
					linePartDescription_1: value?.description_1 ?? "",
					linePartDescription_2: value?.description_2 ?? "",
					linePartUnitName: value?.partUnitName ?? "",
					linePartDesignUnitName: value?.designUnitName ?? "",
					linePartDesignUnitFactor: value?.designUnitFactor ?? 1
				};
			},
			editable: true,
			validate: "required",
			width: 250
		}),
		showInDesignUnit && [
			floatColumn({
				field: "designUnitQuantity",
				headerName: i18n.t("quantity"),
				editable: true,
				validate: "required"
			}),
			textColumn({
				field: "linePartDesignUnitName",
				headerName: i18n.t("design_unit")
			})
		],
		!showInDesignUnit && [
			floatColumn({
				field: "quantity",
				headerName: i18n.t("quantity"),
				editable: true,
				validate: "required"
			}),
			textColumn({
				field: "linePartUnitName",
				headerName: i18n.t("unit")
			})
		],
		enumColumn({
			field: "acquisitionMethod",
			headerName: i18n.t("acquisition_method"),
			enumLabels: getPartRevisionLineAcquisitionMethodLabels(),
			editable: true,
			validate: "required",
			width: 150
		}),
		booleanColumn({
			field: "isPhantom",
			headerName: i18n.t("phantom"),
			editable: true
		}),
		bomLineGroupOptions.length > 0 && singleSelectColumn({
			field: "bomLineGroupId",
			headerName: i18n.t("group"),
			valueOptions: bomLineGroupOptions.map((option) => ({
				value: option.bomLineGroupId,
				label: option.name
			})),
			editable: true,
			width: 150
		}),
		textColumn({
			field: "note",
			headerName: i18n.t("note"),
			editable: true,
			width: 450
		})
	];
}

export function getDefaultRefNo(api: AavoDataGridApi<CatalogPartRevisionBomDataGridRow>): number {
	const currentReferenceNos = api.getRows().map((r) => r.referenceNo);
	if (currentReferenceNos.length === 0) return 1;
	return Math.max(...currentReferenceNos) + 1;
}
